"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Mascot, Button, StartWritingButton, Wordmark, QuarkleLogo, YouTubeModal } from "./ui";
import {
  Sparkle,
  Star,
  Squiggle,
  Ribbon,
  TapeSticker,
  CornerPin,
  OutlineDisplay,
  Asterism,
} from "./ornaments";

/* Three objectively-better rewrites. Each demonstrates a different editorial move:
 *  [0] compression — one concrete image replaces two flat abstractions
 *  [1] specificity — swap vague adjectives for sensory nouns
 *  [2] stakes — internalize character feeling through action
 * Verbose ≠ better. These rewrites each trim OR tighten without inflating.
 */
const LIVE_EXAMPLES = [
  {
    file: "ch04 - the ascent.md",
    original:
      "The dragon was big and scary. It flew through the sky. Eldra was scared but she kept running.",
    diff: [
      { t: "The dragon ", k: "keep" },
      { t: "was big and scary. It flew through the sky", k: "cut" },
      { t: "came down from the clouds like an avalanche with wings", k: "add" },
      { t: ". Eldra ", k: "keep" },
      { t: "was scared but she kept running", k: "cut" },
      { t: "ran anyway", k: "add" },
      { t: ".", k: "keep" },
    ],
    note: "*Three flat sentences into two with stakes.* The avalanche image does the work of \"big and scary\", and \"ran anyway\" is braver than \"was scared but kept running.\"",
    principle: "compression",
  },
  {
    file: "ch07 - the inn at dusk.md",
    original:
      "The old inn was very cold and smelled bad. People were talking loudly. Maren ordered a drink from the bartender.",
    diff: [
      { t: "The ", k: "keep" },
      { t: "old inn was very cold and smelled bad", k: "cut" },
      { t: "inn kept its cold the way cellars do, and its smell the way coats do after rain", k: "add" },
      { t: ". ", k: "keep" },
      { t: "People were talking loudly", k: "cut" },
      { t: "Voices bumped into each other", k: "add" },
      { t: ". Maren ordered ", k: "keep" },
      { t: "a drink from the bartender", k: "cut" },
      { t: "whatever was open", k: "add" },
      { t: ".", k: "keep" },
    ],
    note: "*Specificity over adjectives.* \"Very cold\" tells; \"kept its cold the way cellars do\" shows. \"Whatever was open\" says more about Maren than any backstory would.",
    principle: "specificity",
  },
  {
    file: "ch12 - the letter.md",
    original:
      "Ivo read the letter and felt very sad. He didn't want to believe it. He put it down slowly.",
    diff: [
      { t: "Ivo read the letter ", k: "keep" },
      { t: "and felt very sad. He didn't want to believe it. He put it down slowly", k: "cut" },
      { t: "twice. then a third time, as if the words might behave differently if he kept his eyes on them", k: "add" },
      { t: ".", k: "keep" },
    ],
    note: "*Feeling shown through behavior, not named.* The reader gets \"sad\" by watching him refuse the news. No adjective required.",
    principle: "stakes",
  },
];

/* DiffDoc renders the rewritten paragraph across four semantic states.
 *
 * State machine (kept tiny on purpose — everything else is a derivation):
 *
 *   "read"     → clean original. cut segments render as normal black text;
 *                add segments are display:none so nothing takes phantom
 *                layout space. the reader sees a complete sentence, not
 *                a sentence with invisible gaps.
 *
 *   "editing"  → strike + add appear TOGETHER (one frame). cut segments
 *                fade to 0.32 opacity with a pink line-through; add
 *                segments become display:inline with opacity 0 → 1 and the
 *                pink highlight pad. a single reflow, a single transition.
 *
 *   "accepted" → the sentence becomes its final rewritten self. cut
 *                segments go display:none (remove strike clutter), add
 *                segments become plain black in-body type. no pink.
 *
 *   "rejected" → the original is restored. add segments go display:none,
 *                cut segments lose their strike and return to full opacity.
 *
 * Transitions are opacity-only (cheap) and always start from a committed
 * layout. No animated `display`, no animated `color-mix` backgrounds.
 */
function DiffDoc({ state, example }) {
  return (
    <p
      className="display"
      style={{ fontSize: "clamp(17px,1.3vw,20px)", lineHeight: 1.6, margin: 0 }}
    >
      {example.diff.map((s, i) => {
        if (s.k === "keep") return <span key={i}>{s.t}</span>;

        if (s.k === "cut") {
          const hidden = state === "accepted";
          const struck = state === "editing";
          return (
            <span
              key={i}
              style={{
                display: hidden ? "none" : "inline",
                textDecorationLine: struck ? "line-through" : "none",
                textDecorationColor: "var(--pink)",
                textDecorationThickness: "2px",
                opacity: struck ? 0.32 : 1,
                transition: "opacity 0.4s ease",
              }}
            >
              {s.t}
            </span>
          );
        }

        // k === "add"
        const hidden = state === "read" || state === "rejected";
        const plain = state === "accepted";
        return (
          <span
            key={i}
            style={{
              display: hidden ? "none" : "inline",
              color: plain ? "inherit" : "var(--pink)",
              fontStyle: plain ? "normal" : "italic",
              background: plain ? "transparent" : "color-mix(in oklab, var(--pink) 14%, transparent)",
              padding: plain ? 0 : "0 3px",
              borderRadius: 3,
              opacity: hidden ? 0 : 1,
              transition: "opacity 0.45s ease, background 0.3s ease, color 0.3s ease",
            }}
          >
            {s.t}
          </span>
        );
      })}
    </p>
  );
}

/* LiveEdit choreography.
 *
 *   read      (4.0s) — original sentence, clean. additions are display:none
 *                      so nothing takes phantom layout. buttons dimmed.
 *
 *   editing   (2.6s) — strike, add, and margin-note arrive together in a
 *                      single render. buttons still dimmed (let the reader
 *                      register the suggestion first).
 *
 *   wait      (5.5s) — buttons live, countdown bar draining. user clicks
 *                      accept / keep mine to resolve early.
 *
 *   pressing  (0.4s) — visual "button pressed" interstitial (so the commit
 *                      doesn't feel instant).
 *
 *   resolved  (4.0s) — the diff collapses to its final form (accepted or
 *                      kept). during this window, an undo pill is live.
 *                      click undo → back to wait with a fresh countdown.
 *                      otherwise we advance to the next example.
 *
 * Each phase owns its own timer inside a single useEffect keyed on [phase,
 * variant, waitCycle]. That means any setPhase() triggered by a user click
 * automatically cancels the prior phase's pending timer — no ad-hoc
 * cancellable timer bookkeeping, no stale callbacks. `waitCycle` bumps
 * whenever we re-enter wait (either from editing or from undo) so the
 * countdown bar's CSS animation restarts.
 */
const LIVE_TIMINGS = {
  read: 4000,
  editing: 2600,
  wait: 5500,
  pressing: 400,
  resolved: 4000,
};

function LiveEdit() {
  const [phase, setPhase] = useState("read");        // read | editing | wait | pressing | resolved
  const [resolution, setResolution] = useState(null); // null | "accepted" | "kept"
  const [pressing, setPressing] = useState(null);    // null | "accept" | "keep"
  const [variant, setVariant] = useState(0);
  const [waitCycle, setWaitCycle] = useState(0);      // bumps on every entry into wait (for countdown key)
  const example = LIVE_EXAMPLES[variant];

  const toWait = () => {
    setResolution(null);
    setPressing(null);
    setPhase("wait");
    setWaitCycle((n) => n + 1);
  };

  /* Per-phase timer scheduler. Each phase runs its own setTimeout; the
   * cleanup function clears it whenever `phase` changes (including from
   * a user click). No orphaned callbacks. */
  useEffect(() => {
    if (phase === "read") {
      const t = setTimeout(() => setPhase("editing"), LIVE_TIMINGS.read);
      return () => clearTimeout(t);
    }
    if (phase === "editing") {
      const t = setTimeout(() => {
        setPhase("wait");
        setWaitCycle((n) => n + 1);
      }, LIVE_TIMINGS.editing);
      return () => clearTimeout(t);
    }
    if (phase === "wait") {
      /* Auto-accept if the reader doesn't click within the wait window. */
      const t = setTimeout(() => {
        setPressing("accept");
        setPhase("pressing");
      }, LIVE_TIMINGS.wait);
      return () => clearTimeout(t);
    }
    if (phase === "pressing") {
      /* After the button-press visual, commit the chosen resolution. */
      const t = setTimeout(() => {
        setResolution(pressing === "keep" ? "kept" : "accepted");
        setPhase("resolved");
      }, LIVE_TIMINGS.pressing);
      return () => clearTimeout(t);
    }
    if (phase === "resolved") {
      /* Auto-advance to the next example, unless user clicks undo first. */
      const t = setTimeout(() => {
        setResolution(null);
        setPressing(null);
        setPhase("read");
        setVariant((v) => (v + 1) % LIVE_EXAMPLES.length);
      }, LIVE_TIMINGS.resolved);
      return () => clearTimeout(t);
    }
  }, [phase, variant, waitCycle]);

  const canAcceptReject = phase === "wait";
  const canUndo = phase === "resolved";

  const handleClick = (choice) => {
    if (!canAcceptReject) return;
    setPressing(choice);
    setPhase("pressing");
  };

  const handleUndo = () => {
    if (!canUndo) return;
    toWait();
  };

  /* Derive DiffDoc state from the live machine. */
  const diffState =
    phase === "resolved" ? (resolution === "kept" ? "rejected" : "accepted")
    : phase === "read" ? "read"
    : "editing";

  const noteVisible = phase !== "read";

  const statusLine = (() => {
    if (phase === "read") return "read the original · quarkle is thinking";
    if (phase === "editing") return "quarkle is suggesting an edit";
    if (phase === "wait") return "your call · accept or keep mine";
    if (phase === "pressing") return pressing === "keep" ? "keeping yours…" : "applying…";
    if (resolution === "accepted") return "applied · click undo to revert";
    if (resolution === "kept") return "kept yours · click undo to revisit";
    return "";
  })();

  return (
    <div
      style={{
        background: "var(--paper-3)",
        border: "1.5px solid var(--ink)",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "14px 14px 0 var(--ink)",
        position: "relative",
      }}
    >
      <div
        style={{
          padding: "10px 16px",
          borderBottom: "1.5px solid var(--ink)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--ink)",
          color: "var(--paper)",
        }}
      >
        <span className="mono" style={{ fontSize: 11, display: "inline-flex", gap: 10, alignItems: "center" }}>
          <span style={{ display: "inline-flex", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "#E85B5B" }} />
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "#E8C15B" }} />
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "#8EC56C" }} />
          </span>
          {example.file}
        </span>
        <span className="caps" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: "var(--pink)",
              animation: "qv6-qpulse 1.4s infinite",
            }}
          />
          quarkle · {example.principle}
        </span>
      </div>
      <div style={{ padding: "24px 24px 10px", minHeight: 210 }}>
        <DiffDoc state={diffState} example={example} />
        <div
          style={{
            marginTop: 18,
            padding: "12px 14px",
            background: "var(--paper)",
            border: "1px solid var(--ink)",
            borderRadius: 10,
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: 12,
            opacity: noteVisible ? 1 : 0,
            transform: noteVisible ? "translateY(0)" : "translateY(6px)",
            transition: "opacity 0.45s ease, transform 0.45s ease",
          }}
        >
          <Mascot pose="teaching" size={40} float={false} />
          <div>
            <div className="caps" style={{ color: "var(--pink)", fontSize: 10 }}>quarkle · margin note</div>
            <div className="display" style={{ fontSize: 15, fontStyle: "italic", marginTop: 2 }}>
              {example.note.split("*").map((p, i) =>
                i % 2 ? (
                  <em key={i} style={{ color: "var(--pink)" }}>
                    {p}
                  </em>
                ) : (
                  <span key={i}>{p}</span>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 16px",
          borderTop: "1px solid var(--ink)",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Resolved: show a single status button (accepted/kept) + undo pill.
         * Otherwise: the two decision buttons. */}
        {phase === "resolved" ? (
          <>
            <Button
              variant={resolution === "accepted" ? "pink" : "subtle"}
              size="sm"
              style={{ opacity: 0.9, cursor: "default", pointerEvents: "none" }}
            >
              {resolution === "accepted" ? "✓ accepted" : "× kept yours"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleUndo}
              style={{
                borderStyle: "dashed",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                animation: "qv6-suggest-in 0.35s ease-out",
              }}
            >
              ↶ undo
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="pink"
              size="sm"
              onClick={() => handleClick("accept")}
              style={{
                transform: pressing === "accept" ? "translateY(2px)" : undefined,
                boxShadow: pressing === "accept" ? "2px 2px 0 var(--ink)" : "4px 4px 0 var(--ink)",
                opacity: canAcceptReject ? 1 : 0.5,
                cursor: canAcceptReject ? "pointer" : "default",
                transition: "transform 0.18s ease, box-shadow 0.18s ease, opacity 0.3s ease",
              }}
            >
              accept ⌘⏎
            </Button>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => handleClick("keep")}
              style={{
                transform: pressing === "keep" ? "translateY(2px)" : undefined,
                opacity: canAcceptReject ? 1 : 0.5,
                cursor: canAcceptReject ? "pointer" : "default",
                transition: "transform 0.18s ease, opacity 0.3s ease",
              }}
            >
              keep mine
            </Button>
          </>
        )}

        {/* Countdown to auto-accept (only while waiting for the reader) */}
        {phase === "wait" && (
          <div
            aria-hidden
            style={{
              height: 3,
              width: 80,
              marginLeft: 6,
              background: "color-mix(in oklab, var(--ink) 15%, transparent)",
              borderRadius: 999,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              key={`wait-${waitCycle}`}
              style={{
                position: "absolute",
                inset: 0,
                background: "var(--pink)",
                transformOrigin: "left",
                animation: `qv6-liveedit-countdown ${LIVE_TIMINGS.wait}ms linear forwards`,
              }}
            />
          </div>
        )}

        {/* Countdown to next-example during resolved (so undo has a deadline) */}
        {phase === "resolved" && (
          <div
            aria-hidden
            style={{
              height: 3,
              width: 80,
              marginLeft: 6,
              background: "color-mix(in oklab, var(--ink) 15%, transparent)",
              borderRadius: 999,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              key={`resolved-${variant}-${resolution || ""}`}
              style={{
                position: "absolute",
                inset: 0,
                background: "color-mix(in oklab, var(--ink) 45%, transparent)",
                transformOrigin: "left",
                animation: `qv6-liveedit-countdown ${LIVE_TIMINGS.resolved}ms linear forwards`,
              }}
            />
          </div>
        )}

        <span className="mono" style={{ fontSize: 10, opacity: 0.55, marginLeft: "auto" }}>
          {statusLine}
        </span>
      </div>
    </div>
  );
}

export function Hero() {
  const [demoOpen, setDemoOpen] = useState(false);
  return (
    <section style={{ padding: "40px 40px 80px", position: "relative", overflow: "hidden" }}>
      <YouTubeModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <div className="qv6-hero-deco-draft" aria-hidden style={{ position: "absolute", top: -40, right: -80, opacity: 0.11, pointerEvents: "none", lineHeight: 0.8 }}>
        <OutlineDisplay text="draft." size={320} color="var(--pink)" strokeW={1.8} />
      </div>
      <div className="qv6-hero-deco-quarkle" aria-hidden style={{ position: "absolute", bottom: -60, left: -30, opacity: 0.08, pointerEvents: "none", transform: "rotate(-6deg)" }}>
        <OutlineDisplay text="quarkle" size={280} color="var(--ink)" strokeW={1.6} />
      </div>
      <div aria-hidden style={{ position: "absolute", bottom: 30, left: 60, opacity: 0.55, pointerEvents: "none" }}>
        <Asterism size={26} color="var(--ink)" />
      </div>
      <div aria-hidden style={{ position: "absolute", top: 60, left: "48%", opacity: 0.8, pointerEvents: "none" }}>
        <Asterism size={22} color="var(--clay)" />
      </div>
      <div aria-hidden style={{ position: "absolute", top: 220, right: "42%" }}>
        <Sparkle size={34} color="var(--pink)" style={{ animation: "qv6-sparkle 2.6s ease-in-out infinite" }} />
      </div>
      <div aria-hidden style={{ position: "absolute", bottom: 60, right: 80 }}>
        <Sparkle size={22} color="var(--moss)" style={{ animation: "qv6-sparkle 3.2s ease-in-out infinite" }} />
      </div>

      <div
        className="qv6-hero-meta-row"
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 20,
          paddingBottom: 20,
          borderBottom: "1px solid var(--ink)",
        }}
      >
        <CornerPin>44,632 writers have picked up the pen</CornerPin>
        <div className="qv6-hero-meta-center" style={{ textAlign: "center" }}>
          <span className="mono" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.6 }}>
            · the AI editor for novelists, memoirists & self-publishers ·
          </span>
        </div>
        <span className="mono qv6-hero-meta-right" style={{ fontSize: 11, opacity: 0.6 }}>
          a quiet place to finish the book
        </span>
      </div>

      <div
        className="qv6-hero-grid"
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1.15fr 1fr",
          gap: 56,
          alignItems: "center",
          marginTop: 40,
        }}
      >
        <div className="qv6-hero-copy-side" style={{ position: "relative" }}>
          <div style={{ position: "absolute", top: -18, left: -8 }}>
            <TapeSticker color="var(--lime)" rotate={-4}>dispatch №01 · hello, writer</TapeSticker>
          </div>
          <h1
            className="tanker"
            style={{
              margin: "20px 0 0",
              /* Max is bounded by the nowrap first line: "bad drafts," measures
               * 4.37em in DM Serif Display at this letter-spacing, and the copy
               * column tops out at 676px once the container caps at 1400px —
               * so anything past ~155px gets clipped by the section's
               * overflow:hidden. Keep headroom if you retune this. */
              fontSize: "clamp(58px, 8vw, 144px)",
              lineHeight: 0.88,
              letterSpacing: "-0.045em",
              position: "relative",
            }}
          >
            <span style={{ whiteSpace: "nowrap" }}>bad drafts,</span>
            <br />
            <span style={{ display: "inline-block", position: "relative", margin: "0 0.15em 0 0" }}>
              <span className="display" style={{ fontStyle: "italic", color: "var(--pink)", position: "relative", zIndex: 2 }}>
                good
              </span>
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: -6,
                  right: -6,
                  bottom: "0.08em",
                  height: "0.28em",
                  background: "var(--lime)",
                  zIndex: 1,
                  transform: "rotate(-1.2deg)",
                  borderRadius: 4,
                }}
              />
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  inset: "-8% -6% -6% -4%",
                  border: "3px solid var(--ink)",
                  borderRadius: "52% 46% 58% 44% / 58% 44% 56% 42%",
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
            </span>
            company.
            <span aria-hidden style={{ display: "inline-block", marginLeft: 16, verticalAlign: "middle" }}>
              <Sparkle size={38} color="var(--clay)" />
            </span>
          </h1>

          <div className="qv6-hero-psst-row" style={{ display: "flex", alignItems: "flex-start", gap: 22, marginTop: 30, maxWidth: 600 }}>
            <span
              className="hand"
              style={{
                fontSize: 36,
                color: "var(--pink)",
                lineHeight: 0.9,
                whiteSpace: "nowrap",
                transform: "rotate(-4deg)",
                display: "inline-block",
                marginTop: 8,
              }}
            >
              psst,
            </span>
            <p className="display qv6-hero-psst" style={{ margin: 0, fontSize: 21, lineHeight: 1.5, fontStyle: "italic", opacity: 0.9 }}>
              your novel has been sitting there for{" "}
              <u style={{ textDecorationColor: "var(--pink)", textDecorationThickness: 2 }}>months</u>.
              quarkle is the AI editor built for it: it reads every line, remembers the details, and helps you decide what the next draft needs.
              <span
                style={{
                  opacity: 0.6,
                  display: "block",
                  marginTop: 8,
                  fontStyle: "normal",
                  fontSize: 15,
                  fontFamily: "'Satoshi', sans-serif",
                }}
              >
                (you keep the pen. quarkle keeps the thread.)
              </span>
            </p>
          </div>

          <div className="qv6-hero-cta-row" style={{ display: "flex", gap: 14, marginTop: 36, alignItems: "center", flexWrap: "wrap" }}>
            <StartWritingButton variant="pink" size="xl">start writing →</StartWritingButton>
            <Button variant="ghost" size="lg" onClick={() => setDemoOpen(true)}>▶ watch 60s demo</Button>
            <div className="qv6-rating" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={14} color="var(--clay)" />
              ))}
              <span className="mono" style={{ fontSize: 11, opacity: 0.7 }}>loved by drafters, midnight pantsers, plot-board devotees</span>
            </div>
          </div>
        </div>

        <div className="qv6-liveedit-wrap" style={{ position: "relative" }}>
          <LiveEdit />
          <div className="qv6-liveedit-deco" style={{ position: "absolute", top: -22, left: -30, zIndex: 3 }}>
            <TapeSticker color="var(--lime)" rotate={-6}>watch me read →</TapeSticker>
          </div>
          <div
            className="qv6-liveedit-deco"
            style={{
              position: "absolute",
              bottom: -56,
              left: 20,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <span
              aria-hidden
              style={{ display: "inline-block", width: 110, height: 1, background: "var(--ink)", opacity: 0.7 }}
            />
            <div>
              <span
                className="mono"
                style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.65 }}
              >
                fig. 01 ·
              </span>
              <div className="display" style={{ fontSize: 19, fontStyle: "italic", marginTop: 1, lineHeight: 1.2 }}>
                real-time, real nice.
              </div>
            </div>
          </div>
          <div
            className="qv6-liveedit-deco"
            style={{
              position: "absolute",
              right: -40,
              bottom: -60,
              zIndex: 4,
              animation: "qv6-mascot-float 6s ease-in-out infinite",
            }}
          >
            <Mascot pose="reading" size={130} float={false} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* Five pillars, not eleven tenets. This is the plain-language "what quarkle
 * actually is" statement, up top where a visitor can't miss it — the deeper
 * versions of each pillar live in Features() and MoreFeatures() below. */
export function CorePillars() {
  const pillars = [
    { n: "01", label: "the review room", body: "12 specialist AI passes across copy, line, developmental & continuity craft, reconciled into one set of notes.", color: "var(--pink)" },
    { n: "02", label: "the story atlas", body: "characters, world, and plot: remembered and kept in sync as you write.", color: "var(--moss)" },
    { n: "03", label: "editorial reports", body: "a verdict, a revision plan, a promise ledger, a tension map, from one read.", color: "var(--clay)" },
    { n: "04", label: "chat & brainstorm", body: "a writing partner that reads your manuscript before it answers.", color: "var(--violet)" },
    { n: "05", label: "the drawing room", body: "AI book covers, character portraits, and scenes drawn from the manuscript and Atlas.", color: "var(--pink-deep)" },
  ];
  return (
    <section style={{ padding: "10px 40px 70px", position: "relative" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <CornerPin>what quarkle actually is</CornerPin>
          <h2
            className="tanker"
            style={{ margin: "14px 0 10px", fontSize: "clamp(36px,4.4vw,64px)", lineHeight: 1.04, letterSpacing: "-0.03em" }}
          >
            an AI editor for novelists, memoirists,{" "}
            <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>and self-publishers</span>.
          </h2>
          <p className="display" style={{ fontStyle: "italic", fontSize: 18, opacity: 0.78, maxWidth: 640, margin: "0 auto" }}>
            not a chatbot. five connected tools built around one manuscript, so the book gets finished.
          </p>
        </div>
        <div className="qv6-pillars-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 24 }}>
          {pillars.map((p) => (
            <div key={p.n} style={{ borderTop: `3px solid ${p.color}`, paddingTop: 16 }}>
              <span className="tanker" style={{ fontSize: 34, fontStyle: "italic", color: p.color, lineHeight: 0.85 }}>
                {p.n}
              </span>
              <div className="caps" style={{ marginTop: 8, opacity: 0.85 }}>{p.label}</div>
              <p className="display" style={{ marginTop: 8, fontSize: 15, fontStyle: "italic", lineHeight: 1.5, opacity: 0.85 }}>
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* A real product screenshot, framed like the rest of the paper aesthetic.
 * Used by Features() below — no mocked-up UI, the same shots MoreFeatures()
 * and /features use. */
function FeatureShot({ src, alt, width, height }) {
  return (
    <div
      style={{
        background: "var(--paper-3)",
        border: "1px solid var(--rule)",
        borderRadius: 16,
        padding: 14,
        boxShadow: "8px 8px 0 color-mix(in oklab, var(--ink) 18%, transparent)",
      }}
    >
      <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--rule)" }}>
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          sizes="(max-width: 760px) calc(100vw - 36px), (max-width: 1200px) 45vw, 600px"
          style={{ display: "block", width: "100%", height: "auto" }}
        />
      </div>
    </div>
  );
}

export function Features() {
  const items = [
    {
      tag: "01",
      kicker: "not one prompt, a structured review",
      title: "12 AI passes. one useful set of notes.",
      body: "typos fixed, rhythm tightened, plot holes flagged, continuity checked.",
      visual: (
        <FeatureShot
          src="/images/app/feat-review-room.png"
          alt="A manuscript with a live grammar suggestion popover open, next to the Review Room panel showing the Copy, Line, and Developmental reviewer tiers"
          width={2670}
          height={1770}
        />
      ),
      accent: "var(--pink)",
      href: "/features#review-room",
    },
    {
      tag: "02",
      kicker: "your book's memory, kept properly",
      title: "your manuscript builds its own memory.",
      body: "characters, world, and plot: kept in sync as you write.",
      visual: (
        <FeatureShot
          src="/images/app/feat-atlas-note.png"
          alt="An auto-written character dossier for Noa Kessler with an AI portrait: role, personality, backstory, and arc drawn from the manuscript"
          width={1332}
          height={1656}
        />
      ),
      accent: "var(--moss)",
      href: "/memory",
    },
    {
      tag: "03",
      kicker: "the verdict comes first",
      title: "one report. every angle.",
      body: "a revision plan, a promise ledger, a tension map, from one read.",
      visual: (
        <FeatureShot
          src="/images/app/feat-report.png"
          alt="The Full Assessment's Verdict tab: the verdict, a How It Reads readability sidebar, a chapter-by-chapter tension chart, and the plan in brief"
          width={1826}
          height={1576}
        />
      ),
      accent: "var(--clay)",
      href: "/features#reports",
    },
    {
      tag: "04",
      kicker: "the book becomes visible",
      title: "covers, characters, scenes. drawn from your story.",
      body: "the Drawing Room turns manuscript context and Story Atlas notes into AI book covers, character portraits, and scene illustrations. you review the prompt, choose the style and shape, and keep only what belongs.",
      visual: (
        <FeatureShot
          src="/images/app/feat-drawing-styles.jpg"
          alt="The Drawing Room's style shelf: book and poster, comics and screen, made by hand, and photographic styles, each previewing the same scene"
          width={1812}
          height={1550}
        />
      ),
      accent: "var(--pink-deep)",
      href: "/ai-book-cover-generator",
    },
  ];
  return (
    <section style={{ padding: "60px 40px 80px", position: "relative" }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <CornerPin color="var(--pink)">four reasons quarkle &ne; another chatbox</CornerPin>
        <h2
          className="tanker"
          style={{
            margin: "14px 0 0",
            fontSize: "clamp(48px,6vw,96px)",
            lineHeight: 0.96,
            letterSpacing: "-0.03em",
          }}
        >
          built like a <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>writing room</span>.
        </h2>
      </div>
      {items.map((it, i) => (
        <article
          key={i}
          className="qv6-features-card"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 72,
            alignItems: "center",
            padding: "96px 0",
            borderTop: "1px solid var(--ink)",
            position: "relative",
          }}
        >
          <div style={{ position: "absolute", top: 30, left: "50%", transform: "translateX(-50%)" }}>
            <span
              className="mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                background: "var(--paper)",
                padding: "0 14px",
                opacity: 0.6,
              }}
            >
              chapter {it.tag}
            </span>
          </div>
          <div className="qv6-feature-text" style={{ order: i % 2 === 0 ? 0 : 1, position: "relative" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
              <span
                className="tanker"
                style={{ fontSize: 128, color: it.accent, lineHeight: 0.85, fontStyle: "italic" }}
              >
                {it.tag}
              </span>
              <div>
                <span className="caps" style={{ opacity: 0.6, display: "block" }}>{it.kicker}</span>
                <Squiggle w={160} h={12} color={it.accent} />
              </div>
            </div>
            <h3
              className="tanker"
              style={{
                fontSize: "clamp(40px, 4.8vw, 76px)",
                lineHeight: 1,
                letterSpacing: "-0.025em",
                margin: "14px 0 0",
              }}
            >
              {it.title}
            </h3>
            <p
              className="display"
              style={{
                marginTop: 20,
                fontSize: 19,
                lineHeight: 1.55,
                opacity: 0.88,
                maxWidth: 520,
                fontStyle: "italic",
              }}
            >
              {it.body}
            </p>
            <a
              href={it.href}
              className="mono"
              style={{
                marginTop: 18,
                display: "inline-block",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: `color-mix(in oklab, ${it.accent} 42%, var(--ink))`,
                textUnderlineOffset: 4,
              }}
            >
              see this feature in full →
            </a>
          </div>
          <div className="qv6-feature-visual" style={{ order: i % 2 === 0 ? 1 : 0, position: "relative" }}>
            <div
              className="qv6-ghost-number"
              aria-hidden
              style={{
                position: "absolute",
                top: -60,
                left: i % 2 === 0 ? -20 : "auto",
                right: i % 2 === 0 ? "auto" : -20,
                pointerEvents: "none",
                zIndex: 0,
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                opacity: 0.85,
              }}
            >
              <span
                className="mono"
                style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.55 }}
              >
                no.
              </span>
              <span
                className="tanker"
                style={{
                  fontSize: 160,
                  lineHeight: 0.8,
                  fontStyle: "italic",
                  color: "transparent",
                  WebkitTextStrokeWidth: 1.5,
                  WebkitTextStrokeColor: it.accent,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <div style={{ position: "relative", zIndex: 1 }}>{it.visual}</div>
          </div>
        </article>
      ))}
    </section>
  );
}


/* Invented testimonials, same as the original three: our copy, in a writer's
   voice, about Quarkle. Deliberately NOT drawn from real user conversations,
   even paraphrased; nobody consented to their messages being displayed, so
   nothing here originates from one. Names, genres and draft stages are all
   made up. When editing, vary the register (sentence case next to lowercase,
   four words next to two sentences) and ration the punchlines: eighteen wry
   undercuts in a row read as one copywriter doing a bit. */
const QUOTES = [
  { q: "i cried twice. once when quarkle caught a continuity error from 80 pages back. once when it told me my opening was actually good.", a: "mira o.", r: "fantasy · draft 3", pose: "reading" },
  { q: "it feels like writing with a very smart friend who read the whole thing and also happens to be nice.", lime: true, a: "dan a.", r: "screenwriter · pilot in dev", pose: "teaching" },
  { q: "the first tool that didn't try to turn my voice into an airport novel.", a: "yuki p.", r: "debut sci-fi · 2026", pose: "thinking" },
  { q: "i've stopped keeping a character bible in a separate doc. it just knows.", a: "helena v.", r: "epic fantasy · book 3", pose: "waving" },
  { q: "nothing changes until i say so. after three tools that helpfully overwrote my chapter, that is the whole product.", a: "marcus t.", r: "thriller · draft 2", pose: "reading" },
  { q: "every other AI wants to rewrite my book. this one wants to help me write mine.", lime: true, a: "june k.", r: "literary · revising", pose: "teaching" },
  { q: "It's the only AI that has actually read my whole book. The others say they have.", a: "sasha l.", r: "romance · book 5", pose: "thinking" },
  { q: "pasted in 40 chapters expecting it to choke. it didn't.", a: "pete w.", r: "space opera · 200k words", pose: "waving" },
  { q: "the margin notes are worth it on their own honestly", a: "ana g.", r: "ya fantasy · draft 1", pose: "reading" },
  { q: "It flagged that a scene contradicted something my character said ten chapters earlier. It was right.", lime: true, a: "colin f.", r: "mystery · book 2", pose: "teaching" },
  { q: "finally something that doesn't lose the plot after chapter ten", a: "tessa m.", r: "serial fiction · ongoing", pose: "thinking" },
  { q: "I reject half the edit cards and that's fine. The half I accept are why I'm still subscribed.", a: "owen b.", r: "horror · draft 4", pose: "waving" },
  { q: "it's good at telling me WHY a chapter isn't working, not just that it isn't", a: "leah c.", r: "memoir · draft 2", pose: "reading" },
  { q: "I don't dread revision anymore. That's new for me.", lime: true, a: "vic s.", r: "crime · book 3", pose: "teaching" },
  { q: "keeps three POV characters straight better than i do, and i invented them", a: "noor a.", r: "epic fantasy · outlining", pose: "thinking" },
  { q: "i was fully ready to hate it", a: "felix j.", r: "satire · draft 1", pose: "teaching" },
  { q: "finished my first novel in eleven years. i'm not saying it's because of quarkle. i'm not saying it isn't.", a: "rob d.", r: "first novel · done", pose: "reading" },
  { q: "My writing group teased me about using it. Two of them use it now.", lime: true, a: "meg r.", r: "historical · book 1", pose: "waving" },
];

export function Quotes() {
  const qs = QUOTES.map((q, i) => ({
    ...q,
    rot: [-2, 1.5, -1, 1, -1.5, 2][i % 6],
    color: q.lime ? "var(--lime)" : "var(--paper-3)",
  }));
  return (
    <section
      style={{
        padding: "100px 40px 120px",
        background: "var(--paper-2)",
        borderTop: "1.5px solid var(--ink)",
        borderBottom: "1.5px solid var(--ink)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div aria-hidden style={{ position: "absolute", top: 40, right: 40, opacity: 0.55, pointerEvents: "none" }}>
        <Asterism size={24} color="var(--ink)" />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 20,
          marginBottom: 40,
        }}
      >
        <div>
          <span className="caps" style={{ color: "var(--pink)" }}>what writers tell us</span>
          <h2
            className="tanker"
            style={{
              fontSize: "clamp(52px, 6.6vw, 110px)",
              margin: "10px 0 0",
              lineHeight: 0.96,
              letterSpacing: "-0.03em",
              maxWidth: "18ch",
            }}
          >
            the only reviews{" "}
            <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>we care about</span>.
          </h2>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} size={20} color="var(--clay)" />
          ))}
          <span className="display" style={{ fontStyle: "italic", fontSize: 20 }}>
            a small, stubborn group of writers · growing quietly
          </span>
        </div>
      </div>
      <div className="qv6-quotes-scroll">
      {/* Focusable so the cards past the fold are reachable by keyboard: arrow
          keys scroll the nearest scrollable ancestor, which is this grid itself
          on mobile (horizontal carousel) and the rail on desktop (vertical). */}
      <div
        className="qv6-quotes-grid"
        tabIndex={0}
        role="group"
        aria-label="What writers tell us. Use the arrow keys to scroll for more."
        style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 22 }}
      >
        {qs.map((q, i) => (
          <figure
            key={i}
            style={{
              margin: 0,
              padding: 24,
              background: q.color,
              color: q.color === "var(--lime)" ? "#1A1410" : "var(--ink)",
              border: q.color === "var(--lime)" ? "1.5px solid #1A1410" : "1.5px solid var(--ink)",
              borderRadius: 10,
              boxShadow: `${q.rot < 0 ? "-" : ""}6px 8px 0 var(--ink)`,
              transform: `rotate(${q.rot}deg)`,
              position: "relative",
            }}
          >
            <span
              aria-hidden
              style={{
                position: "absolute",
                top: -12,
                left: "50%",
                transform: "translateX(-50%)",
                width: 18,
                height: 18,
                borderRadius: 999,
                background: "var(--pink)",
                border: "1px solid var(--ink)",
                boxShadow: "0 2px 0 rgba(0,0,0,0.35)",
              }}
            />
            <span
              className="display"
              style={{ fontSize: 66, color: "var(--pink)", lineHeight: 0.3, display: "block" }}
            >
              “
            </span>
            <blockquote
              className="display"
              style={{ margin: "10px 0 0", fontSize: 18, lineHeight: 1.38, fontStyle: "italic" }}
            >
              {q.q}
            </blockquote>
            <figcaption
              style={{
                marginTop: 18,
                display: "flex",
                gap: 10,
                alignItems: "center",
                borderTop: "1px dashed var(--rule)",
                paddingTop: 12,
              }}
            >
              <Mascot pose={q.pose} size={36} float={false} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{q.a}</div>
                <div className="mono" style={{ fontSize: 10, opacity: 0.6 }}>{q.r}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
      </div>
    </section>
  );
}

const MORE_FEATURES = [
  {
    title: "the highlight bar",
    body: "select a line, pick expand, embellish, concise, or restructure. preview the diff. accept or reject. nothing changes until you say so.",
    accent: "var(--pink)",
    img: "/images/app/feat-highlight-bar.png",
    imgAlt: "The Highlight Bar over a selected sentence, with Expand, Embellish, Concise, and Restructure",
    width: 1530,
    height: 900,
    href: "/features#highlight-bar",
  },
  {
    title: "any model, one subscription",
    body: "claude, gpt, grok, kimi, gemini. pick per chapter or task, or leave it on auto and let quarkle choose for you. no separate accounts, no separate bills.",
    accent: "var(--sky)",
    img: "/images/app/feat-models.jpg",
    imgAlt: "The model picker showing Claude Sonnet 4.6, GPT-5.4, Grok 4.3, and Kimi K2.5 as selectable options",
    width: 1074,
    height: 720,
    href: "/best-ai-models-for-writing",
  },
  {
    title: "keep every draft within reach",
    body: "auto-checkpoints roughly every 250 words. compare any two, restore an old one. your current draft is saved first, always.",
    accent: "var(--moss)",
    img: "/images/app/feat-versions-wide.png",
    imgAlt: "A before-and-after comparison between two checkpoints, with removed text struck through in red and added text highlighted in green",
    width: 1228,
    height: 437,
    href: "/features#version-history",
  },
  {
    title: "see the shape of your year",
    body: "track writing days, streaks, daily averages, and monthly goals without turning every session into a word-count contest.",
    accent: "var(--violet)",
    img: "/images/app/feat-milestones.png",
    imgAlt: "A year-long writing activity heatmap showing which days had writing sessions",
    width: 1470,
    height: 392,
    href: "/features#milestones",
  },
];

function FeaturePreviewCard({ f }) {
  return (
    <article
      className="qv6-feature-preview-card"
      style={{ "--feature-accent": f.accent, background: "var(--paper-3)", border: "1px solid var(--rule)", borderRadius: 14, boxShadow: "4px 4px 0 color-mix(in oklab, var(--ink) 22%, transparent)", overflow: "hidden", display: "flex", flexDirection: "column" }}
    >
      <div
        className="qv6-feature-preview-image-wrap"
        style={{
          background: "var(--paper-2)",
          borderBottom: "1px solid var(--rule)",
          aspectRatio: "16 / 9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 10,
        }}
      >
        <Image
          className="qv6-feature-preview-image"
          src={f.img}
          alt={f.imgAlt}
          width={f.width}
          height={f.height}
          sizes="(max-width: 760px) calc(100vw - 36px), (max-width: 1200px) 45vw, 450px"
          style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain", borderRadius: 5 }}
        />
      </div>
      <div style={{ padding: "24px 26px 26px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span aria-hidden style={{ width: 11, height: 11, borderRadius: 999, background: f.accent, display: "inline-block" }} />
          {f.tag && <span className="caps" style={{ fontSize: 9 }}>{f.tag}</span>}
        </div>
        <h3 className="tanker" style={{ fontSize: 32, margin: "12px 0 0", lineHeight: 1.05 }}>{f.title}</h3>
        <p className="display" style={{ fontSize: 16, fontStyle: "italic", lineHeight: 1.55, opacity: 0.85, margin: "12px 0 20px" }}>{f.body}</p>
        <a href={f.href} className="mono qv6-feature-preview-link" style={{ marginTop: "auto", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: `color-mix(in oklab, ${f.accent} 42%, var(--ink))`, textUnderlineOffset: 4 }}>
          see this feature →
        </a>
      </div>
    </article>
  );
}

export function MoreFeatures() {
  return (
    <section style={{ padding: "80px 40px 100px", background: "var(--paper)", borderTop: "1.5px solid var(--ink)", borderBottom: "1.5px solid var(--ink)" }}>
      <div style={{ textAlign: "center", marginBottom: 44 }}>
        <CornerPin>and there's more in the room</CornerPin>
        <h2
          className="tanker"
          style={{ margin: "14px 0 0", fontSize: "clamp(44px,5.6vw,88px)", lineHeight: 0.96, letterSpacing: "-0.03em" }}
        >
          more ways to stay <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>in control</span>.
        </h2>
        <p className="mono" style={{ fontSize: 11, opacity: 0.5, marginTop: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          real product screenshots · no mockups
        </p>
      </div>
      <div
        className="qv6-more-features-grid"
        style={{
          maxWidth: 940,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 24,
        }}
      >
        {MORE_FEATURES.map((f, i) => (
          <FeaturePreviewCard key={i} f={f} />
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <a
          href="/features"
          className="display"
          style={{
            fontStyle: "italic",
            fontSize: 19,
            color: "var(--ink)",
            textDecoration: "none",
            borderBottom: "1.5px solid var(--pink)",
            paddingBottom: 2,
          }}
        >
          see everything in the room →
        </a>
      </div>
    </section>
  );
}

export function Pricing() {
  const [cycle, setCycle] = useState("monthly");
  const plans = [
    {
      name: "writer",
      price: "free",
      subLine: "no card, no time limit",
      tag: "everything you need to draft.",
      feats: [
        "1M tokens daily",
        "chat & the review room",
        "story atlas: build and maintain it yourself",
        "5 Drawing Room pictures a day · keep 10 per book",
        "version control: checkpoint and restore any draft",
        "milestones: streaks & daily progress",
        "unlimited books, unlimited drafts",
        "export to plain text",
      ],
      cta: "start writing free",
      highlight: false,
    },
    {
      name: "quarkle pro",
      price: cycle === "monthly" ? "$4.99" : "$120",
      sub: cycle === "monthly" ? "first month · then $19.99" : "per year · save 50%",
      tag: "for the book you're finishing",
      feats: [
        "self-evolving story atlas, kept in sync automatically",
        "200x the usage limits",
        "the best writing models, per scene",
        "12 specialist AI review passes",
        "50 Drawing Room pictures a day · no fixed gallery limit",
        "open expression: mature themes, violence, no content wall",
        "priority reading speed · priority support",
      ],
      cta: cycle === "monthly" ? "start pro · $4.99 first month" : "start pro · $120/yr",
      highlight: true,
    },
    {
      name: "studio",
      price: "let's talk",
      subLine: "for writing rooms & teams",
      tag: "co-writers, custom personas, shared memory.",
      feats: [
        "everything in pro",
        "shared book memory across seats",
        "custom editor personas",
        "early features · direct line to the team",
      ],
      cta: "email team@quarkle.ai",
      highlight: false,
      mailto: true,
    },
  ];
  return (
    <section style={{ padding: "120px 40px", position: "relative" }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <CornerPin>pricing · cozy & fair</CornerPin>
        <h2
          className="tanker"
          style={{
            margin: "14px 0 10px",
            fontSize: "clamp(56px,6.8vw,120px)",
            lineHeight: 0.95,
            letterSpacing: "-0.035em",
          }}
        >
          pick a seat in <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>the room</span>.
        </h2>
        <p className="display" style={{ fontStyle: "italic", fontSize: 20, opacity: 0.75 }}>
          start free. stay for the room. no tricks, no lock-in.
        </p>
        <div
          style={{
            marginTop: 24,
            display: "inline-flex",
            gap: 4,
            padding: 4,
            background: "var(--paper-3)",
            border: "1px solid var(--ink)",
            borderRadius: 999,
          }}
        >
          {[
            { k: "monthly", label: "monthly" },
            { k: "yearly", label: "yearly · save 50%" },
          ].map((o) => {
            const on = cycle === o.k;
            return (
              <button
                key={o.k}
                onClick={() => setCycle(o.k)}
                className="mono"
                style={{
                  all: "unset",
                  cursor: "pointer",
                  padding: "8px 16px",
                  borderRadius: 999,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: on ? "#fff" : "var(--ink)",
                  background: on ? "var(--pink)" : "transparent",
                  fontWeight: on ? 600 : 500,
                  transition: "all 0.2s ease",
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="qv6-pricing-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 22, maxWidth: 1200, margin: "0 auto" }}>
        {plans.map((p, i) => {
          const hi = p.highlight;
          return (
            <div
              key={i}
              style={{
                background: hi ? "#1A1410" : "var(--paper-3)",
                color: hi ? "#F8F1DF" : "var(--ink)",
                border: "1.5px solid var(--ink)",
                borderRadius: 14,
                padding: "30px 26px 26px",
                position: "relative",
                boxShadow: hi ? "10px 10px 0 var(--pink)" : "8px 8px 0 var(--ink)",
                transform: hi ? "translateY(-8px)" : "none",
              }}
            >
              {hi && (
                <div style={{ position: "absolute", top: -14, left: 26 }}>
                  <Ribbon color="var(--lime)" rotate={-4} fg="#1A1410">most writers pick this</Ribbon>
                </div>
              )}
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: -8,
                  top: "50%",
                  width: 16,
                  height: 16,
                  borderRadius: 999,
                  background: "var(--paper)",
                  border: "1px solid var(--ink)",
                  transform: "translateY(-50%)",
                }}
              />
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  right: -8,
                  top: "50%",
                  width: 16,
                  height: 16,
                  borderRadius: 999,
                  background: "var(--paper)",
                  border: "1px solid var(--ink)",
                  transform: "translateY(-50%)",
                }}
              />
              <div className="caps" style={{ opacity: 0.7 }}>{p.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                <span className="tanker" style={{ fontSize: 64, lineHeight: 0.9 }}>{p.price}</span>
                {p.sub && <span className="mono" style={{ fontSize: 17, opacity: 1, fontWeight: 600 }}>{p.sub}</span>}
                {p.subLine && <span className="mono" style={{ fontSize: 11, opacity: 0.55, width: "100%" }}>{p.subLine}</span>}
              </div>
              <p
                className="display"
                style={{ margin: "6px 0 20px", fontStyle: "italic", fontSize: 17, opacity: 0.85 }}
              >
                {p.tag}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", display: "grid", gap: 10 }}>
                {p.feats.map((f, j) => (
                  <li
                    key={j}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: 10,
                      alignItems: "start",
                      borderBottom: "1px dashed rgba(127,127,127,0.3)",
                      paddingBottom: 10,
                    }}
                  >
                    <span style={{ color: hi ? "var(--lime)" : "var(--pink)", marginTop: 4 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24">
                        <path
                          d="M4 12 L10 18 L20 6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span style={{ fontSize: 14 }}>{f}</span>
                  </li>
                ))}
              </ul>
              {p.mailto ? (
                <Button
                  variant={hi ? "lime" : "primary"}
                  size="md"
                  onClick={() => { window.location.href = "mailto:team@quarkle.ai?subject=Quarkle%20Studio"; }}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {p.cta} →
                </Button>
              ) : (
                <StartWritingButton variant={hi ? "lime" : "primary"} size="md" style={{ width: "100%", justifyContent: "center" }}>
                  {p.cta} →
                </StartWritingButton>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 40, textAlign: "center" }}>
        <span className="mono" style={{ fontSize: 11, opacity: 0.6 }}>
          all plans: your work is yours. export anytime. no training on your words, ever.
        </span>
      </div>
    </section>
  );
}

export function FAQ() {
  const qa = [
    { q: "is quarkle going to write my book for me?", a: "god no. you're going to write your book. quarkle just refuses to let you quit at chapter three." },
    { q: "will quarkle flatten my voice?", a: "it shouldn't, but you stay the judge. every change arrives as a preview you can accept, reject, or rewrite yourself." },
    { q: "what if i hate the suggestion?", a: "hit keep mine. quarkle won't sulk. it genuinely doesn't have feelings. probably." },
    { q: "can quarkle make a cover for my book?", a: "yes. the Drawing Room reads the book or the Story Atlas notes you choose, writes an editable image prompt, and draws book covers, character portraits, or scenes. keep the takes you want and attach one as the cover on your library shelf." },
    { q: "does quarkle train on my book?", a: "no. we don't train models on your manuscript, and we don't sell it. approved providers process only what a feature needs to run, and you can export your book anytime." },
    { q: "what does pro actually add?", a: "open expression for mature scenes, every model in chat instead of just auto, the deeper developmental and full assessment reports, and 200x the daily usage. the review room, inline edits, and version history are already free." },
    { q: "what can i import and export?", a: "import Word, PDF, Markdown, or plain text. export Word, PDF, Markdown, or plain text, chapter by chapter or as one document. for Scrivener, compile to Word and import that." },
    { q: "what does 'quarkle' even mean?", a: "honestly, we made it up. it sounded like a good name for an alien pal, the kind of friend you'd let read your draft before your mother does. the dictionary will catch up." },
  ];
  return (
    <section style={{ padding: "120px 40px", position: "relative" }}>
      <div aria-hidden style={{ position: "absolute", top: 40, right: 40, opacity: 0.6, pointerEvents: "none" }}>
        <Asterism size={22} color="var(--ink)" />
      </div>
      <div className="caps" style={{ color: "var(--pink)", marginBottom: 14 }}>the honest part</div>
      <h2
        className="tanker"
        style={{
          fontSize: "clamp(52px, 6.6vw, 120px)",
          margin: 0,
          lineHeight: 0.96,
          letterSpacing: "-0.03em",
          position: "relative",
        }}
      >
        questions people{" "}
        <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>actually</span> ask.
      </h2>
      <div className="qv6-faq" style={{ marginTop: 48, borderTop: "1.5px solid var(--ink)" }}>
        {qa.map((it, i) => (
          <details key={i} style={{ borderBottom: "1.5px solid var(--ink)", padding: "22px 0" }}>
            <summary
              style={{
                cursor: "pointer",
                listStyle: "none",
                display: "flex",
                justifyContent: "space-between",
                gap: 20,
                alignItems: "center",
              }}
            >
              <span style={{ display: "inline-flex", gap: 18, alignItems: "baseline" }}>
                <span className="mono" style={{ fontSize: 13, opacity: 0.45 }}>
                  q.{String(i + 1).padStart(2, "0")}
                </span>
                <span className="tanker" style={{ fontSize: "clamp(22px, 2.4vw, 34px)", letterSpacing: "-0.015em" }}>
                  {it.q}
                </span>
              </span>
              <span className="mono" style={{ fontSize: 22, opacity: 0.5 }}>+</span>
            </summary>
            <p
              className="display"
              style={{
                margin: "14px 0 0 60px",
                fontSize: 21,
                fontStyle: "italic",
                color: "var(--pink)",
                maxWidth: 760,
              }}
            >
              {it.a}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}


export function Footer() {
  return (
    <footer
      style={{
        padding: "56px 40px 36px",
        borderTop: "1.5px solid var(--ink)",
        background: "var(--paper-3)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          bottom: -20,
          right: -20,
          opacity: 0.25,
          pointerEvents: "none",
          lineHeight: 0.8,
        }}
      >
        <OutlineDisplay text="quarkle" size={160} color="var(--ink)" strokeW={1.2} />
      </div>
      <div
        className="qv6-footer-grid"
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr",
          gap: 28,
        }}
      >
        <div className="qv6-footer-brand">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <QuarkleLogo size={52} color="var(--ink)" />
            <Wordmark size={42} />
          </div>
          <p
            className="display"
            style={{ fontSize: 17, opacity: 0.8, marginTop: 14, maxWidth: 340, fontStyle: "italic", lineHeight: 1.4 }}
          >
            a writing friend who happens to be software. made slowly, by people who care about sentences.
          </p>
          <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <TapeSticker color="var(--lime)" rotate={-2}>made in evanston &amp; the himalayas</TapeSticker>
          </div>
        </div>
        {[
          ["product", [
            ["the editor", "/"],
            ["features", "/features"],
            ["book cover generator", "/ai-book-cover-generator"],
            ["quarkle pro", "/pro"],
            ["pricing", "/pricing"],
            ["memory", "/memory"],
          ]],
          ["craft", [
            ["stories", "/blog"],
            ["compare", "/vs"],
          ]],
          ["company", [
            ["contact", "mailto:team@quarkle.ai"],
            ["feedback", "/feedback"],
          ]],
          ["fine print", [
            ["privacy", "/privacy"],
            ["terms", "/terms"],
          ]],
        ].map(([h, items], i) => (
          <div key={i}>
            <div className="caps" style={{ marginBottom: 14, opacity: 0.6 }}>{h}</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
              {items.map(([label, href], j) => (
                <li key={j} style={{ fontSize: 14 }}>
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noreferrer" : undefined}
                    style={{ color: "var(--ink)", textDecoration: "none", opacity: 0.82 }}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div
        className="qv6-footer-bottom"
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          marginTop: 44,
          paddingTop: 20,
          borderTop: "1px dashed var(--rule)",
          flexWrap: "wrap",
          gap: 14,
        }}
      >
        <span className="mono" style={{ fontSize: 11, opacity: 0.6 }}>
          © quarkle · 2026 · all sentences reserved
        </span>
        <span className="mono" style={{ fontSize: 11, opacity: 0.6 }}>
          made with too much tea · · · · · typeset in dm serif display + satoshi
        </span>
      </div>
    </footer>
  );
}
